import type {
  ComponentPropsWithoutRef,
  Dispatch,
  PropsWithChildren,
  RefObject,
  SetStateAction,
} from 'react';
import { useCallback, useEffect, useRef } from 'react';
import {
  useAccountRoleTracking,
  useCurrentPlanTracking,
} from '@frameio/shared/plans/hooks';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { noop, ThemeProvider, Tooltip } from '@frameio/vapor';
import { ModalType } from '../../modal/components';
import { usePlanManagementModalContext } from '../../modal/hooks';
import {
  getTierConfig,
  onKeyDownKeys,
  segmentTrackingValues,
  SubscriptionUpdateSource,
  themeProviderBrand,
  TOOLTIP_TRACKING_DELAY_MS,
} from '../config';
import type { FeatureInput } from '../hooks/useFeatureGatePlanSuggestion';
import { useFeatureGatePlanSuggestion } from '../hooks/useFeatureGatePlanSuggestion';
import { useIsPublicShare } from '../hooks/useIsPublicShare';
import { usePlanFeatureInputFromSuggestion } from '../hooks/usePlanFeatureInputFromSuggestion';
import styles from './FeatureGateModalButtonWithTooltip.module.css';
import { FeatureGateTooltipTitle } from './FeatureGateTooltipTitle';
import { sharedButtonProps, UpgradeModalButton } from './UpgradeModalButton';

const iconSize = '28px';

const iconStyles = {
  height: iconSize,
  width: iconSize,
};

export type FeatureGateLinkWithTooltipProps = PropsWithChildren<{
  feature: FeatureInput;
  isTooltipShowing?: boolean;
  onModalButtonClick?: () => void;
  placement?: ComponentPropsWithoutRef<typeof Tooltip>['placement'];
  setIsUpgradeModalButtonActive?: Dispatch<SetStateAction<boolean>>;
  shouldOpenUpgradeModal?: boolean;
  shouldOverrideFeatureGate?: boolean;
  shouldRenderTooltip?: boolean;
  tooltipAnchorRef?: RefObject<HTMLElement | undefined | null>;
  wrapperStyles?: React.CSSProperties;
}>;

/**
 * Wraps both the FeatureGate and FeatureGateLabel so that either component
 * will show the tooltip and open the plan upgrade modal.
 */
export function FeatureGateModalButtonWithTooltip({
  children,
  feature,
  onModalButtonClick = noop,
  placement = 'top-start',
  setIsUpgradeModalButtonActive = noop,
  shouldOpenUpgradeModal = true,
  shouldOverrideFeatureGate = false,
  shouldRenderTooltip = true,
  isTooltipShowing,
  tooltipAnchorRef,
  wrapperStyles,
}: FeatureGateLinkWithTooltipProps) {
  const isPublicShare = useIsPublicShare();
  const featureGatePlanSuggestion = useFeatureGatePlanSuggestion(feature);
  const planManagementModalContext = usePlanManagementModalContext();

  const currentPlanTracking = useCurrentPlanTracking();
  const accountRoleTracking = useAccountRoleTracking();

  const track = useTrackUnsafe();

  const trackingTimer = useRef<number | undefined>(undefined);

  const planFeatureInputFromSuggestion =
    usePlanFeatureInputFromSuggestion(feature);

  const handleActive = useCallback(() => {
    setIsUpgradeModalButtonActive(true);
    window.clearTimeout(trackingTimer.current);
    if (shouldRenderTooltip) {
      trackingTimer.current = window.setTimeout(() => {
        track('feature-gate-tooltip-activated', {
          ...currentPlanTracking,
          ...accountRoleTracking,
          feature:
            segmentTrackingValues.features[planFeatureInputFromSuggestion],
          feature_gate_type: segmentTrackingValues.types.tooltip,
        });
      }, TOOLTIP_TRACKING_DELAY_MS);
    }
  }, [
    accountRoleTracking,
    currentPlanTracking,
    planFeatureInputFromSuggestion,
    setIsUpgradeModalButtonActive,
    shouldRenderTooltip,
    track,
  ]);

  const handleInactive = useCallback(() => {
    setIsUpgradeModalButtonActive(false);
    window.clearTimeout(trackingTimer.current);
  }, [setIsUpgradeModalButtonActive]);

  useEffect(() => {
    return function unmount() {
      window.clearTimeout(trackingTimer.current);
    };
  }, []);

  if (
    !featureGatePlanSuggestion ||
    featureGatePlanSuggestion?.includedInCurrentPlan ||
    shouldOverrideFeatureGate
  ) {
    return children;
  }

  const plan = featureGatePlanSuggestion.plan;
  const tierConfig = getTierConfig(plan.tier);

  const shouldDisableButton = isPublicShare || !shouldOpenUpgradeModal;

  return (
    <div className={styles.wrapper} style={wrapperStyles}>
      <ThemeProvider brand={themeProviderBrand}>
        <Tooltip
          adornmentBefore={
            <ThemeProvider brand={themeProviderBrand}>
              <tierConfig.icon
                style={{
                  ...iconStyles,
                  color: tierConfig.color,
                }}
              />
            </ThemeProvider>
          }
          anchorRef={tooltipAnchorRef}
          delayIn={0}
          isShowing={isTooltipShowing}
          placement={placement}
          shouldRender={shouldRenderTooltip || !!tooltipAnchorRef?.current}
          title={<FeatureGateTooltipTitle feature={feature} />}
        >
          <UpgradeModalButton
            {...sharedButtonProps}
            onBlur={handleInactive}
            onClick={(event) => {
              if (shouldDisableButton) return;
              planManagementModalContext?.setSelectedPlanWithFeature({
                ...plan,
                feature: planFeatureInputFromSuggestion,
                featureGateType: segmentTrackingValues.types.tooltip,
                modalType: ModalType.UPGRADE,
                subscriptionUpdateSource: SubscriptionUpdateSource.FEATURE_GATE,
              });
              onModalButtonClick();
              event.currentTarget.blur();
            }}
            onFocus={handleActive}
            onKeyDown={(event) => {
              if (shouldDisableButton) return;
              if (onKeyDownKeys.has(event.key)) {
                planManagementModalContext?.setSelectedPlanWithFeature({
                  ...plan,
                  feature: planFeatureInputFromSuggestion,
                  featureGateType: segmentTrackingValues.types.tooltip,
                  modalType: ModalType.UPGRADE,
                  subscriptionUpdateSource:
                    SubscriptionUpdateSource.FEATURE_GATE,
                });
                onModalButtonClick();
              }
            }}
            onMouseEnter={handleActive}
            onMouseLeave={handleInactive}
          />
        </Tooltip>
      </ThemeProvider>
      {children}
    </div>
  );
}
